import React from 'react';
import { useHistory } from 'react-router';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { trackUserProfilePageClick } from 'Shared/tracking/tracking';

// Types
import { Member, OrganizationRoleType } from 'Shared/types/Member.type';

// Components
import {
  WpDropdown,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownToggle,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';

type Props = {
  member: Member;
  handleEditMember: Function;
  handleRemoveUser: Function;
  handleChangeRole: Function;
};

const MemberActions = ({ member, handleEditMember, handleRemoveUser, handleChangeRole }: Props) => {
  const history = useHistory();
  const { user: currentUser } = useCurrentUser();
  const isAdmin = member.organizationRole === OrganizationRoleType.admin;

  const showRemoveUserOption = !member?.piUserGuid;
  const isThirdPartyUser = !member?.primary;

  const preventBubbleEvent = e => e.stopPropagation();

  const handleViewProfileClick = () => {
    trackUserProfilePageClick(currentUser, member.slug);
    history.push(PageUrlsUtils.makeUserProfilePageUrl(member.slug));
  };

  const handleEditClick = () => {
    handleEditMember(member);
  };

  const handleRemoveUserClick = () => {
    handleRemoveUser(member);
  };

  const handleRevokeAdminAccessClick = () => {
    handleChangeRole(member);
  };

  return (
    <div
      onClick={e => {
        preventBubbleEvent(e);
      }}
    >
      <WpDropdown placement="bottom-end">
        <WpDropdownToggle>
          <WpButton variant="tertiary" qaTest={`admin-member-dropdown-${member.email}`}>
            <WpIcon name="ellipsis-h-alt" />
          </WpButton>
        </WpDropdownToggle>
        <WpDropdownMenu>
          <WpDropdownMenuItem onClick={handleViewProfileClick}>
            <WpIcon name="user" />
            View profile
          </WpDropdownMenuItem>
          <WpDropdownMenuItem onClick={handleEditClick}>
            <WpIcon name="pencil" />
            Edit member
          </WpDropdownMenuItem>
          {isAdmin || (showRemoveUserOption && <WpDropdownMenuDivider />)}
          {isAdmin && !isThirdPartyUser && (
            <WpDropdownMenuItem onClick={handleRevokeAdminAccessClick}>
              <WpIcon name="minus-circle" />
              Revoke admin access
            </WpDropdownMenuItem>
          )}
          {showRemoveUserOption && (
            <WpDropdownMenuItem
              color="danger"
              onClick={handleRemoveUserClick}
              qaTest={`admin-member-dropdown-remove-${member.email}`}
            >
              <WpIcon name="times" />
              Remove member
            </WpDropdownMenuItem>
          )}
        </WpDropdownMenu>
      </WpDropdown>
    </div>
  );
};

export default MemberActions;
